// RefillNudgesPanel — small card on the doctor Dashboard that lists any
// patients whose prescription is ≥25 days old and who verified their portal
// link recently. Each entry has a one-tap "WhatsApp" button that opens
// wa.me with a pre-filled refill message (in the doctor's own words) and
// a Dismiss button.
//
// Populated by the portal-verify hook in server.py (`refill_nudges`
// collection). Nothing to schedule — the doctor sees it when they log in.

import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BellRing, MessageCircle, X, Loader2 } from "lucide-react";
import { openWhatsApp } from "../lib/whatsapp";
import { CLINIC } from "../lib/clinic";

export default function RefillNudgesPanel({ onOpenPatient }) {
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/refill-nudges?status=open");
      setNudges(r.data?.nudges || []);
    } catch (e) {
      console.debug("[refill-nudges silent]", e?.message || e);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const mark = async (n, status) => {
    try {
      await api.post(`/refill-nudges/${n.nudge_id}/mark`, { status });
      setNudges((prev) => prev.filter((x) => x.nudge_id !== n.nudge_id));
      toast.success(status === "sent" ? "Marked as sent" : "Nudge dismissed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update nudge");
    }
  };

  const sendWhatsApp = (n) => {
    if (!n.patient_mobile) { toast.error("No mobile on file"); return; }
    const firstName = (n.patient_name || "").split(/\s+/)[0] || "there";
    // Short, friendly, in the doctor's voice — the doctor can edit before
    // sending. Keeps this fully patient-facing, not a bot message.
    const msg =
      `Namaste ${firstName}, this is ${CLINIC.name}.\n` +
      `It looks like your last prescription (${n.days_old_at_trigger}+ days ago) may be running out. ` +
      `Please book a follow-up so we can review your progress and renew any medicines you need.\n` +
      `Thank you!`;
    openWhatsApp(n.patient_mobile, msg);
    mark(n, "sent");
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-3 text-xs text-slate-400 flex items-center gap-2" data-testid="refill-nudges-loading">
        <Loader2 size={12} className="animate-spin" /> Loading refill nudges…
      </div>
    );
  }
  if (!nudges.length) return null;  // hide entirely when queue is empty

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="refill-nudges-panel">
      <div className="flex items-center gap-2 mb-2">
        <BellRing size={14} className="text-amber-700" />
        <div className="text-sm font-semibold text-amber-900" data-testid="refill-nudges-count">
          Time-to-refill nudges ({nudges.length})
        </div>
      </div>
      <div className="text-[10px] text-amber-800/80 mb-2 leading-snug">
        Patients whose prescription is 25+ days old and who opened their portal recently. One tap sends them a friendly refill reminder.
      </div>
      <div className="space-y-1.5 max-h-56 overflow-y-auto">
        {nudges.map((n) => (
          <div key={n.nudge_id} className="flex items-center gap-2 bg-white border border-amber-100 rounded px-2.5 py-1.5" data-testid={`refill-nudge-${n.nudge_id}`}>
            <button
              type="button"
              onClick={() => onOpenPatient?.(n.patient_id)}
              className="flex-1 min-w-0 text-left hover:underline"
              title="Open patient"
            >
              <div className="text-[12px] font-semibold text-slate-800 truncate">{n.patient_name || "—"}</div>
              <div className="text-[10px] text-slate-500 truncate">
                {n.days_old_at_trigger} days · Rx {n.prescription_id}
                {n.issued_by && <span className="ml-1">· {n.issued_by}</span>}
              </div>
            </button>
            <button
              type="button"
              onClick={() => sendWhatsApp(n)}
              className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid={`refill-nudge-wa-${n.nudge_id}`}
              title="Send refill WhatsApp"
            >
              <MessageCircle size={11} /> WhatsApp
            </button>
            <button
              type="button"
              onClick={() => mark(n, "dismissed")}
              className="text-slate-400 hover:text-rose-600"
              title="Dismiss"
              data-testid={`refill-nudge-dismiss-${n.nudge_id}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
